import React, {
    memo,
    MouseEvent,
    useCallback,
    useContext,
    useMemo,
    useState,
} from 'react'
import { Link } from 'react-router-dom'
import {
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    Fade,
    Divider,
    IconButton,
} from '@mui/material'
import { AppContext } from '../context'
import {
    DeleteIcon,
    DeployIcon,
    EditIcon,
    MoreVertIcon,
    QrIcon,
    ViewsIcon,
    VisibilityIcon,
    DownloadIcon,
} from '../components'
import { translate } from '../i18n'
import { ProjectMenuProps } from './ProjectMenu.types'

function ProjectMenu({
    paramTenantId,
    project,
    variant = 'list',
    onPreview,
    onQr,
    onDeploy,
    onDelete,
}: ProjectMenuProps) {
    const context = useContext(AppContext)
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

    const isGrantedUpdate = useMemo(
        () => context.isGrantedAny(['TProjectsUpdate', 'ProjectsUpdate']),
        [context]
    )
    const isGrantedDelete = useMemo(
        () => context.isGrantedAny(['TProjectsDelete', 'ProjectsDelete']),
        [context]
    )

    const onClickedOptions = useCallback((event: MouseEvent<HTMLElement>) => {
        event.stopPropagation()
        setAnchorEl(event.currentTarget)
    }, [])

    const onClickedClose = useCallback(() => {
        setAnchorEl(null)
    }, [])

    const preview = useCallback(() => {
        onClickedClose()
        onPreview(project)
    }, [onPreview, onClickedClose, project])

    const deploy = useCallback(() => {
        onClickedClose()
        onDeploy(project)
    }, [onDeploy, onClickedClose, project])

    const qr = useCallback(() => {
        onClickedClose()
        onQr(project)
    }, [onQr, onClickedClose, project])

    const deleteLocal = useCallback(() => {
        onClickedClose()
        onDelete(project)
    }, [onDelete, onClickedClose, project])

    return (
        <>
            <IconButton
                aria-label="options"
                color="default"
                size="small"
                onClick={onClickedOptions}
            >
                <MoreVertIcon />
            </IconButton>
            {anchorEl && (
                <Menu
                    id="projects-item"
                    anchorEl={anchorEl}
                    keepMounted
                    onClose={onClickedClose}
                    open
                    TransitionComponent={Fade}
                >
                    {isGrantedUpdate && variant === 'list' && (
                        <MenuItem
                            component={Link}
                            to={`${
                                paramTenantId ? `/tenants/${paramTenantId}` : ''
                            }/projects/${project.id}/edit`}
                            onClick={onClickedClose}
                            dense
                        >
                            <ListItemIcon>
                                <EditIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.edit')}
                            </Typography>
                        </MenuItem>
                    )}
                    <MenuItem
                        component={Link}
                        to={`${
                            paramTenantId ? `/tenants/${paramTenantId}` : ''
                        }/projects/${project.id}/views`}
                        onClick={onClickedClose}
                        dense
                    >
                        <ListItemIcon>
                            <ViewsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {translate('project_views.title')}
                        </Typography>
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={preview} dense>
                        <ListItemIcon>
                            <VisibilityIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <Typography variant="inherit">
                            {translate('buttons.preview')}
                        </Typography>
                    </MenuItem>
                    {isGrantedUpdate && project.content?.dev && (
                        <MenuItem onClick={deploy} dense>
                            <ListItemIcon>
                                <DeployIcon fontSize="small" color="primary" />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.deploy')}
                            </Typography>
                        </MenuItem>
                    )}
                    {!!project.content?.prod && !!project.qr_url && (
                        <MenuItem onClick={qr} dense>
                            <ListItemIcon>
                                <QrIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.qr')}
                            </Typography>
                        </MenuItem>
                    )}
                    {!!project.content?.prod && !!project.qr_url && (
                        <MenuItem component="a" href={project.qr_url} download target="_blank" rel="noopener noreferrer" dense>
                            <ListItemIcon>
                                <DownloadIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.download_qr')}
                            </Typography>
                        </MenuItem>
                    )}
                    {isGrantedDelete && variant === 'list' && <Divider />}
                    {isGrantedDelete && variant === 'list' && (
                        <MenuItem onClick={deleteLocal} dense>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <Typography variant="inherit">
                                {translate('buttons.delete')}
                            </Typography>
                        </MenuItem>
                    )}
                </Menu>
            )}
        </>
    )
}

export default memo(ProjectMenu)
